import { useEffect, useRef } from 'react';
import type { NotificationDto } from '@/api/types/notifications';

// Fan-out for live notifications. useNotifications (the bell's data layer) emits every
// live push here so any mounted component can react — show a toast, refetch data — without
// prop-drilling through the bell. Only live pushes are emitted, never the initial load.
type Listener = (n: NotificationDto) => void;

const listeners = new Set<Listener>();

export function emitNotification(n: NotificationDto): void {
  // Snapshot so a listener that unsubscribes mid-dispatch doesn't mutate the set we're iterating.
  for (const l of [...listeners]) {
    try {
      l(n);
    } catch {
      /* one listener throwing must not stop the rest */
    }
  }
}

export function subscribeNotifications(cb: Listener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

// Subscribe a component to live notifications for the duration of its mount. The latest
// handler is always invoked (no stale closures) and the subscription is set up once.
export function useNotificationEffect(handler: Listener): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => subscribeNotifications((n) => ref.current(n)), []);
}
